<script setup>
    import {onMounted, ref, watch} from "vue"

    const props = defineProps({
        stats: Object
    })

    const element = ref(null),
          progressBar = ref(null),
          inView = ref(false)

    function circle(percentage) {
        return ((percentage / 100) * 100 * Math.PI) + ',9999'
    }

    onMounted(() => {
        const observer = new IntersectionObserver(([entry]) => {
            inView.value = entry.isIntersecting
        })
        observer.observe(element.value)

        if (window?.reduced_motion) {
            for (let item of element.value.getElementsByClassName('progress__bar')) {
                item.setAttribute('stroke-dasharray', circle(item.getAttribute('data-percentage')))
            }
        }
    })

    watch(inView, (value) => {
        const progressBars = element.value.getElementsByClassName('progress__bar')

        if (!window?.reduced_motion) {
            if (value) {
                setTimeout(() => {
                    for (let item of progressBars) {
                        item.setAttribute('stroke-dasharray', circle(item.getAttribute('data-percentage')))
                    }
                }, 300)
            } else {
                for (let item of progressBars) {
                    item.setAttribute('stroke-dasharray', circle(0))
                }
            }
        }
    })
</script>

<template>
    <div ref="element" class="flex flex-wrap gap-8 sm:gap-0 justify-center">
        <template v-for="( stat, index ) in stats" :key="index">
            <div
                class="progress"
                :class="{
                    'opacity-0' : !inView,
                    'opacity-100' : inView
                }"
            >
                <figure class="progress__inner relative inline-block" >
                    <figcaption class="progress__text-container w-full h-full absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 p-[10%]">
                        <div class="w-full h-full flex flex-col justify-center items-center border border-cintra-grey dark:border-white rounded-full p-[10%] text-center">
                            <span class="text-3xl font-bold dark:text-white">{{ stat.selling_point_value }}</span>
                            <span class="font-bold dark:text-white text-sm">{{ stat.selling_point_text }}</span>
                        </div>
                    </figcaption>
                    <svg
                        class="w-full progress__circle"
                        viewBox="0 0 106 106"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        aria-hidden="true"
                    >
                        <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stop-color=""/>
                                <stop offset="100%" stop-color=""/>
                            </linearGradient>
                        </defs>
                        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                            <g transform="translate(-17.000000, -17.000000)">
                                <circle stroke="transparent" stroke-width="3%" fill-rule="nonzero" cx="70" cy="70" r="50"></circle>
                                <path :data-percentage="stat.percentage" class="progress__bar" :stroke-dasharray="((0 / 100) * 100 * Math.PI) + ',9999'" d="M70,120 C97.6142375,120 120,97.6142375 120,70 C120,42.3857625 97.6142375,20 70,20 C42.3857625,20 20,42.3857625 20,70 C20,97.6142375 42.3857625,120 70,120 Z" stroke="url('#gradient')" stroke-width="3%" fill-rule="nonzero" transform="translate(70,70) rotate(-125) translate(-70,-70) "></path>
                            </g>
                        </g>
                    </svg>
                </figure>
            </div>
        </template>
    </div>
</template>
<style lang="scss">
    #gradient {
        stop {
            &:first-of-type {
                stop-color: theme('colors.cintra-blue.DEFAULT');
            }
            &:last-of-type {
                stop-color: theme('colors.cintra-blue.light');
            }
        }
    }

    .progress {
        @apply w-full;
        @apply md:w-1/3;
        @apply lg:w-1/2;
        @apply flex;
        @apply justify-center;
        @apply items-center;

        &:nth-of-type(1) {
            //@apply sm:-mb-12;
            //@apply sm:mb-0;
            @apply sm:justify-start;
            @apply md:justify-center;
            //@apply lg:-mb-14;

            .progress__inner {
                @apply lg:w-full;
                @apply lg:max-w-[320px];
                @apply md:min-w-[250px];
            }
            .progress__circle {
                transform: scaleX(-1) rotate(-55deg);
            }

            .progress__bar {
                transition-delay: 0.1s;
            }
        }
        &:nth-of-type(2) {
            //@apply sm:-my-12;
            @apply md:my-0;
            @apply sm:justify-end;
            @apply md:justify-center;

            .progress__inner {
                @apply lg:w-10/12;
                @apply lg:max-w-[300px];
                @apply md:min-w-[250px];
            }

            .progress__circle {
                transform: scaleX(-1) rotate(180deg);
            }

            .progress__bar {
                transition-delay: 0.3s;
            }
        }
        &:nth-of-type(3) {
            //@apply sm:-mt-12;
            //@apply md:mt-0;
            @apply sm:justify-start;
            @apply md:justify-center;
            @apply lg:justify-end;
            //@apply lg:-mt-6;

            .progress__inner {
                @apply lg:w-8/12;
                @apply lg:max-w-[280px];
                @apply md:min-w-[250px];
            }

            .progress__circle {
                transform: scaleX(-1) rotate(30deg);
            }

            .progress__bar {
                transition-delay: 0.2s;
            }
        }
        &__bar {
            transition: 1s ease-in-out;
        }
    }
</style>
